import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { UpdateService } from '../update.service';
import { UserService } from '../../services/user.service';
import { AppUser, ProjectRole } from '../../models/user.model';
import { ProjectSubscription, SubscriptionPricingConfig } from '../../models/subscription.model';
import { SubscriptionService } from '../../services/subscription.service';
import { SubscriptionDialogComponent } from '../../users/subscription-dialog/subscription-dialog.component';
import { ApiConfigService } from '../../services/api-config.service';
import { TeamService } from '../../services/team.service';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { ProjectContextService } from '../../services/project-context.service';
import { ProjectSummary } from '../../models/api-dto.model';
import { CreateProjectDialogComponent } from '../create-project-dialog/create-project-dialog.component';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent implements OnInit, OnDestroy {

  updateSubscription?: Subscription;
  userSubscription?: Subscription;
  subSubscription?: Subscription;
  projectSubscriptionSub?: Subscription;
  userProjectsSub?: Subscription;

  currentUser: AppUser | null = null;
  projectSubscription: ProjectSubscription | null = null;
  ownerName: string = 'Owner';

  currentProject: ProjectSummary | null = null;
  userProjects: ProjectSummary[] = [];

  constructor(
    private updateService: UpdateService,
    public userService: UserService,
    public subService: SubscriptionService,
    public apiConfigService: ApiConfigService,
    private preferencesService: UserPreferencesService,
    public projectContext: ProjectContextService,
    private teamService: TeamService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdRef: ChangeDetectorRef
  ) { }

  get activeProjectId(): string {
    return this.currentProject?.id || this.projectContext.getActiveProjectId();
  }

  get brandLandingRoute(): string {
    return this.preferencesService.getLandingPageRoute(this.activeProjectId);
  }

  get isLiveMode(): boolean {
    return true;
  }

  get isApiOnline(): boolean {
    return this.apiConfigService.isApiOnline;
  }

  get apiBaseUrl(): string {
    return this.apiConfigService.apiBaseUrl;
  }

  checkHealth(): void {
    this.snackBar.open('Checking Sprinta Cloud API connection...', undefined, { duration: 1500 });
    this.apiConfigService.checkApiHealth().subscribe(res => {
      this.snackBar.open(res.message, 'OK', { duration: 3000 });
      this.cdRef.detectChanges();
    });
  }

  get pricing(): SubscriptionPricingConfig {
    return this.subService.pricing;
  }

  ngOnInit(): void {
    this.userSubscription = this.userService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.ownerName = user?.displayName || user?.email || 'Owner';
      this.cdRef.detectChanges();
    });

    this.subSubscription = this.subService.subscription$.subscribe((sub: ProjectSubscription) => {
      this.projectSubscription = sub;
      this.cdRef.detectChanges();
    });

    this.projectSubscriptionSub = this.projectContext.currentProject$.subscribe(project => {
      this.currentProject = project;
      this.cdRef.detectChanges();
    });

    this.userProjectsSub = this.projectContext.userProjects$.subscribe(projects => {
      this.userProjects = projects;
      this.cdRef.detectChanges();
    });

    // Make sure user projects are loaded
    this.projectContext.loadUserProjects(false).subscribe();

    this.updateSubscription = timer(1800).subscribe(() => { 
      this.updateService.checkForUpdate();
      if (this.updateSubscription != null) {
        this.updateSubscription.unsubscribe();
      }
    });      
  }

  switchProject(projectId: string): void {
    if (!projectId || projectId === this.activeProjectId) return;
    this.projectContext.switchProject(projectId);
  }

  openCreateProjectDialog(): void {
    this.dialog.open(CreateProjectDialogComponent, {
      width: '500px',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });
  }

  navigateToProjects(): void {
    this.router.navigateByUrl('/projects');
  }

  get isOwner(): boolean {
    return this.userService.isOwner();
  }

  get isExpiringSoon(): boolean {
    return this.projectSubscription?.status === 'expiring_soon';
  }

  get isGracePeriod(): boolean {
    return this.projectSubscription?.status === 'grace_period';
  }

  get isBlocked(): boolean {
    return this.projectSubscription?.status === 'blocked';
  }

  openSubscriptionModal(tabIndex = 1): void {
    this.dialog.open(SubscriptionDialogComponent, {
      width: '740px',
      data: { initialTab: tabIndex }
    });
  }

  get canViewBacklog(): boolean {
    return this.userService.canViewBacklog();
  }

  get canViewSprint(): boolean {
    return this.userService.canViewSprint();
  }

  get canViewTeams(): boolean {
    return this.userService.canViewTeams();
  }

  roleBadge(role?: ProjectRole): string {
    switch (role || this.currentUser?.projectRole) {
      case 'owner': return 'Owner';
      case 'developer': return 'Developer';
      case 'internal_stakeholder': return 'Stakeholder (Internal)';
      case 'external_stakeholder': return 'Stakeholder (External)';
      default: return 'Member';
    }
  }

  formatProjectRole(role?: string): string {
    switch (role) {
      case 'owner': return 'Owner';
      case 'developer': return 'Developer';
      case 'internal_stakeholder': return 'Internal';
      case 'external_stakeholder': return 'External';
      default: return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member';
    }
  }

  roleColor(role?: ProjectRole): string {
    switch (role || this.currentUser?.projectRole) {
      case 'owner': return '#e65100';
      case 'developer': return '#1565c0';
      case 'internal_stakeholder': return '#6a1b9a';
      case 'external_stakeholder': return '#2e7d32';
      default: return '#555';
    }
  }

  ngOnDestroy(): void {
    this.updateSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.subSubscription?.unsubscribe();
    this.projectSubscriptionSub?.unsubscribe();
    this.userProjectsSub?.unsubscribe();
  }
}
